import type { FC, ReactPortal, PropsWithChildren } from 'react'
import { useEffect, useState, useMemo } from 'react'

import { AnimatePresence, motion } from 'motion/react'
import ReactDOM from 'react-dom'

import { A11yText } from 'components/A11yText'
import { IconButton } from 'components/IconButton'
import { IconClose } from 'components/icons'

import { Header, Container, ModalBody } from './styles'

type ModalHeaderProps = {
  onClose: () => void
  closeTitle: string
  title?: string
}
export const ModalHeader = ({
  onClose,
  closeTitle,
  title,
}: ModalHeaderProps): JSX.Element => {
  return (
    <Header>
      <div>
        {title && <A11yText as="h1">{title}</A11yText>}
        <IconButton
          title={closeTitle}
          onClick={onClose}
          iconSize="small"
          icon={IconClose}
        />
      </div>
    </Header>
  )
}

export const Modal: FC<
  PropsWithChildren<
    ModalHeaderProps & { noScroll?: boolean; show: boolean; noHeader?: boolean }
  >
> = ({
  children,
  onClose,
  show,
  closeTitle,
  title,
  noScroll = false,
  noHeader = false,
}): ReactPortal | null => {
  const MODAL_ROOT_ID = 'modal-root'

  const [root, setRoot] = useState<HTMLElement>()

  const instance = useMemo(() => document.createElement('div'), [])

  const createModalRoot = (): HTMLDivElement => {
    const element = document.createElement('div')
    element.setAttribute('id', MODAL_ROOT_ID)
    document.body.appendChild(element)
    setRoot(element)
    return element
  }

  const handleKeyPress = (event: KeyboardEvent): void => {
    if (event.code === 'Escape') {
      closeModal()
    }
  }

  const openModal = (): void => {
    document.addEventListener('keydown', handleKeyPress, false)
    if (instance) root?.appendChild(instance)
  }

  const closeModal = (): void => {
    document.removeEventListener('keydown', handleKeyPress, false)
    document.body.classList.remove('overflow--hidden')
    onClose()
    if (instance) {
      root?.removeChild(instance)
    }
  }

  useEffect(() => {
    // only create the root element if it doesn't already exist.
    setRoot(document.getElementById(MODAL_ROOT_ID) || createModalRoot())
    // cleanup on unmount.
    return closeModal
  }, [])

  useEffect(() => {
    if (show) {
      document.body.classList.add('overflow--hidden')
      openModal()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  const pageVariants = {
    in: {
      opacity: 1,
      transition: {
        duration: 0.2,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.5,
        delay: 1.2,
      },
    },
  }

  const pageTransition = {
    type: 'tween',
    ease: [0.22, 1, 0.36, 1],
  }

  const onAnimationComplete = (): void => {
    if (!show) {
      closeModal()
    }
  }

  const modalContent = (
    <AnimatePresence mode="wait">
      {show && (
        <motion.div
          initial="out"
          animate="in"
          exit="out"
          variants={pageVariants}
          transition={pageTransition}
          onAnimationComplete={onAnimationComplete}
        >
          <Container noScroll={noScroll} role="dialog" title={title}>
            {!noHeader && (
              <ModalHeader
                onClose={() => closeModal()}
                closeTitle={closeTitle}
                title={title}
              />
            )}
            {children}
          </Container>
        </motion.div>
      )}
    </AnimatePresence>
  )

  return ReactDOM.createPortal(modalContent, instance)
}

export { ModalBody }
